import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ChevronDown, BarChart3, Mic, Volume2 } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { useLanguage } from '../context/LanguageContext';
import ChatHeader from '../components/FullChat/ChatHeader';
import ChatInput from '../components/ChatInput';
import ChatMessages from '../components/ChatMessages';
import QuickOptions from '../components/QuickOptions';
import openaiTTS from '../utils/openaiTTS';


import BusinessOptionsModal from '../components/FullChat/BusinessOptionsModal';
import SecondBusinessSelectionModal from '../components/FullChat/SecondBusinessSelectionModal';
import PrimaryBusinessSelectionModal from '../components/FullChat/PrimaryBusinessSelectionModal';
import QuickOptionsPopup from '../components/QuickOptionsPopup';
import ChatFooter from '../components/ChatFooter';
import ProductTour from '../components/FullChat/ProductTour';
import LanguageSelector from '../components/FullChat/LanguageSelector';
import WelcomePopup from '../components/FullChat/WelcomePopup';
import SidebarMenu from '../components/FullChat/SidebarMenu';
import DebugPanel from '../components/DebugPanel';
import MobileHamburgerMenu from '../components/FullChat/MobileHamburgerMenu';
import BusinessTypeButtons from '../components/FullChat/BusinessTypeButtons';

// Custom hooks
import { useChatState } from '../hooks/useChatState';
import { useModalState } from '../hooks/useModalState';
import { useBusinessLogic } from '../hooks/useBusinessLogic';
import { useChatActions } from '../hooks/useChatActions';
import { useQuickOptions } from '../hooks/useQuickOptions';
import { allBusinessOptions } from '../data/optionsData';
import ChatHistoryService from '../services/chatHistoryService';
import AllChatsModal from '../components/FullChat/AllChatsModal';

import '../styles/ChatPage.css';
import '../css/components/FullChat/FullChat.css';

function FullChatPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Theme context
  const { theme, currentThemeName, toggleTheme, isToggling } = useTheme();
  
  // Auth context
  const { isAdmin, isAuthenticated, saveChat, user, updateProfile, logout } = useAuth();

  // Custom hooks
  const chatScrollContainerRef = useRef(null);
  const chatState = useChatState({ mobileScrollContainerRef: chatScrollContainerRef });
  const modalState = useModalState();
  const businessLogic = useBusinessLogic();
  const quickOptionsState = useQuickOptions();
  
  // Destructure state from hooks
  const {
    messages, setMessages,
    inputMessage, setInputMessage,
    isLoading, setIsLoading,
    businessType, setBusinessType,
    currentSessionId, setCurrentSessionId,
    currentConversationId, setCurrentConversationId,
    showScrollButton,
    messagesEndRef, chatMessagesRef,
    scrollToBottom, handleNewChat
  } = chatState;

  const {
    showBusinessOptionsModal, setShowBusinessOptionsModal, showWelcomePopup, showProductTour, showLanguageSelector,
    handleCloseWelcomePopup, resetWelcomePopup,
    handleStartProductTour, handleCompleteProductTour,
    handleCloseBusinessOptionsModal: originalHandleCloseBusinessOptionsModal, setShowLanguageSelector
  } = modalState;

  // Custom close handler
  const handleCloseBusinessOptionsModal = () => {
    originalHandleCloseBusinessOptionsModal(false);
  };

  // State for second business selection modal
  const [showSecondBusinessModal, setShowSecondBusinessModal] = useState(false);
  
  // State for primary business selection modal
  const [showPrimaryBusinessModal, setShowPrimaryBusinessModal] = useState(false);
  
  // State for chat history modal
  const [showChatHistory, setShowChatHistory] = useState(false);
  // Mobile menu open state — hide FAB when menu is open
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Mobilde sağdaki yüzen ? butonunu gösterme (yardım header'da)
  const [isNarrowViewport, setIsNarrowViewport] = useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const handler = () => setIsNarrowViewport(mql.matches);
    mql.addEventListener('change', handler);
    handler();
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Voice recording state from ChatInput
  const [voiceState, setVoiceState] = useState({
    isRecording: false,
    isAutoRecording: false,
    hasStartedManually: false,
    isAutoDisabled: false
  });

  // TTS state management
  const [ttsState, setTtsState] = useState({
    isReading: false,
    isPaused: false,
    isLoading: false
  });

  // Input mode state (voice or keyboard)
  const [inputMode, setInputMode] = useState('voice');

  // Add bounce animation CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes bounce {
        0%, 20%, 50%, 80%, 100% {
          transform: translateX(-50%) translateY(0);
        }
        40% {
          transform: translateX(-50%) translateY(-5px);
        }
        60% {
          transform: translateX(-50%) translateY(-3px);
        }
      }
      
      @keyframes pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
        }
        50% {
          opacity: 0.5;
          transform: scale(1.1);
        }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);


  const {
    showQuickOptionsPopup,
    setShowQuickOptionsPopup,
    handleQuickOptionSelect: quickOptionsHandler
  } = quickOptionsState;

  // Handle quick option selection with actual functionality
  const handleQuickOptionSelect = useCallback((option) => {
    const result = quickOptionsHandler(option);
    
    if (result) {
      switch (result.action) {
        case 'prompt':
          // Set the input message and trigger send
          setInputMessage(result.prompt);
          // Trigger the send message function
          setTimeout(() => {
            const sendButton = document.querySelector('[data-testid="send-button"]') || 
                              document.querySelector('button[type="submit"]') ||
                              document.querySelector('button:has(svg)');
            if (sendButton) {
              sendButton.click();
            }
            // Auto-scroll to bottom after sending message
            setTimeout(() => {
              scrollToBottom();
            }, 200);
          }, 100);
          break;
          
        case 'help':
          // Show help modal or navigate to help
          alert(t('fullchat.helpSupportMessage'));
          break;
          
        default:
          console.log('Unknown quick option action:', result.action);
      }
    }
  }, [quickOptionsHandler, setInputMessage, scrollToBottom]);

  const {
    userBusinesses, loadingBusinesses,
    randomBusinessOptions, changingOptions,
    changingBusinessOptions, quickOptions, allQuickOptions,
    isAtBottom,
    fetchUserBusinesses, scrollBusinessModal,
    setQuickOptions
  } = businessLogic;
  
  // Language context
  const { currentLanguage, changeLanguage, t } = useLanguage();

  // State to force re-render when user data changes
  const [userUpdateTrigger, setUserUpdateTrigger] = useState(0);
  
  // Debug panel state
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  // Check if user already has a business type
  const userHasBusinessType = user?.business_type && user.business_type.trim() !== '';
  
  // Debug logging
  console.log('🔍 FullChatPage Debug:', {
    isAuthenticated: isAuthenticated(),
    user: user,
    userHasBusinessType,
    userBusinessType: user?.business_type,
    userSecondBusinessType: user?.second_business_type,
    messagesLength: messages.length,
    currentBusinessType: businessType,
    currentConversationId: currentConversationId,
    currentSessionId: currentSessionId,
    isPrimaryBusinessActive: businessType === user?.business_type,
    isSecondBusinessActive: businessType === user?.second_business_type,
    showPrimaryBusinessButton: isAuthenticated() && !user?.business_type && messages.length === 0 && !businessType
  });

  // Initialize business type from user profile if available
  useEffect(() => {
    console.log('🔄 Business type initialization effect triggered:', {
      userHasBusinessType,
      currentBusinessType: businessType,
      userBusinessType: user?.business_type,
      user: user
    });
    
    if (userHasBusinessType && !businessType) {
      console.log('🔄 Setting business type from user profile:', user.business_type);
      setBusinessType(user.business_type);
    }
  }, [user, userHasBusinessType, businessType]);

  // Show welcome popup on first load (when user has never seen it)
  useEffect(() => {
    const welcomeSeen = localStorage.getItem('oaklandAI-welcome-seen');
    if (!welcomeSeen) {
      const timer = setTimeout(() => {
        resetWelcomePopup();
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [resetWelcomePopup]);

  // Listen for product tour events
  useEffect(() => {
    const handleTourEvent = () => {
      handleStartProductTour();
    };

    window.addEventListener('startProductTour', handleTourEvent);
    return () => window.removeEventListener('startProductTour', handleTourEvent);
  }, [handleStartProductTour]);

  // Handle keyboard events for welcome popup
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showWelcomePopup) {
        handleCloseWelcomePopup();
      }
    };

    if (showWelcomePopup) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [showWelcomePopup, handleCloseWelcomePopup]);

  // Handle click outside to close welcome popup
  const handlePopupBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      handleCloseWelcomePopup();
    }
  };

  // Sync local user state with AuthContext
  useEffect(() => {
    if (user) {
      setUserUpdateTrigger(prev => prev + 1);
    }
  }, [user?.id]); // Only depend on user.id to avoid infinite loops

  // Load conversation from URL parameter
  useEffect(() => {
    const conversationIdFromUrl = searchParams.get('conversationId');
    
    if (conversationIdFromUrl && isAuthenticated() && conversationIdFromUrl !== String(currentConversationId)) {
      console.log('📋 Loading conversation from URL:', conversationIdFromUrl);
      
      const loadConversation = async () => {
        try {
          setIsLoading(true);
          
          // Get conversation messages
          const result = await ChatHistoryService.getConversationMessages(conversationIdFromUrl, 0, 100);
          
          if (result && result.messages && result.messages.length > 0) {
            // Convert messages to the format expected by the chat interface
            const formattedMessages = result.messages.map(msg => ({
              type: msg.role === 'user' ? 'user' : 'answer',
              content: msg.content,
              timestamp: new Date(msg.created_at),
              id: msg.id
            }));
            
            // Set messages and conversation ID (convert to number if needed)
            setMessages(formattedMessages);
            const convId = parseInt(conversationIdFromUrl) || conversationIdFromUrl;
            setCurrentConversationId(convId);
            
            // Get conversation details to set business type
            try {
              const conversations = await ChatHistoryService.getConversations(100);
              const conversation = conversations.find(conv => 
                String(conv.id) === String(conversationIdFromUrl) || 
                String(conv.conversation_id) === String(conversationIdFromUrl)
              );
              
              if (conversation && conversation.business_type) {
                setBusinessType(conversation.business_type);
              }
            } catch (err) {
              console.error('Error fetching conversation details:', err);
            }
            
            // Remove conversationId from URL after loading
            searchParams.delete('conversationId');
            setSearchParams(searchParams, { replace: true });
            
            console.log('✅ Conversation loaded successfully:', conversationIdFromUrl);
          } else {
            console.log('⚠️ No messages found for conversation:', conversationIdFromUrl);
            // Still remove the parameter even if no messages found
            searchParams.delete('conversationId');
            setSearchParams(searchParams, { replace: true });
          }
        } catch (error) {
          console.error('❌ Error loading conversation:', error);
          alert(t('fullchat.failedToLoadConversation'));
          // Remove the parameter on error too
          searchParams.delete('conversationId');
          setSearchParams(searchParams, { replace: true });
        } finally {
          setIsLoading(false);
        }
      };
      
      loadConversation();
    }
  }, [searchParams, isAuthenticated, currentConversationId, setMessages, setCurrentConversationId, setBusinessType, setIsLoading, setSearchParams]);

  // Function to refresh user data from localStorage
  const refreshUserData = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUserUpdateTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
  };

  // Function to refresh user data from backend
  const refreshUserFromBackend = async () => {
    try {
      if (!isAuthenticated()) return;
      
      const response = await fetch(`${getApiBaseUrl()}/api/auth/profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('user', JSON.stringify(userData.user));
        setUserUpdateTrigger(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error refreshing user data from backend:', error);
    }
  };

  // Chat actions hook - defined after all dependencies are available
  const chatActions = useChatActions(
    setMessages, setBusinessType, currentConversationId,
    setCurrentConversationId, setCurrentSessionId,
    businessType, messages, setIsLoading, saveChat, user, refreshUserFromBackend, inputMode
  );

  // Enhanced business type select handler with auto-scroll
  const handleBusinessTypeSelectWithScroll = useCallback(async (type) => {
    await chatActions.handleBusinessTypeSelect(type);
    // Auto-scroll to bottom after business type change
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleBusinessTypeSelect, scrollToBottom]);

  // Enhanced add second business handler with auto-scroll
  const handleAddSecondBusinessWithScroll = useCallback(async (businessType) => {
    await chatActions.handleAddSecondBusiness(businessType);
    // Auto-scroll to bottom after adding second business
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleAddSecondBusiness, scrollToBottom]);

  // Enhanced add primary business handler with auto-scroll
  const handleAddPrimaryBusinessWithScroll = useCallback(async (businessType) => {
    await chatActions.handleAddPrimaryBusiness(businessType);
    // Auto-scroll to bottom after adding primary business
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleAddPrimaryBusiness, scrollToBottom]);

  // Enhanced remove second business handler with auto-scroll
  const handleRemoveSecondBusinessWithScroll = useCallback(async () => {
    await chatActions.handleRemoveSecondBusiness();
    // Auto-scroll to bottom after removing second business
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleRemoveSecondBusiness, scrollToBottom]);

  // Enhanced remove primary business handler with auto-scroll
  const handleRemovePrimaryBusinessWithScroll = useCallback(async () => {
    await chatActions.handleRemovePrimaryBusiness();
    // Auto-scroll to bottom after removing primary business
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleRemovePrimaryBusiness, scrollToBottom]);

  // Enhanced send message handler with auto-scroll
  const handleSendMessageWithScroll = useCallback(async (message) => {
    await chatActions.handleSendMessage(message, inputMode);
    // Auto-scroll to bottom after sending message (single attempt)
    setTimeout(() => {
      scrollToBottom();
    }, 200);
  }, [chatActions.handleSendMessage, scrollToBottom, inputMode]);

  // Enhanced quick option handler with auto-scroll
  const handleQuickOptionWithScroll = useCallback(async (option) => {
    await chatActions.handleQuickOption(option);
    // Auto-scroll to bottom after quick option selection (single attempt)
    setTimeout(() => {
      scrollToBottom();
    }, 300);
  }, [chatActions.handleQuickOption, scrollToBottom]);

  // Listen for storage changes (when user data is updated in other tabs/windows)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user') {
        refreshUserData();
      }
    };

    const handleUserDataUpdate = (e) => {
      console.log('🔄 User data update event received:', e.detail);
      refreshUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('userDataUpdated', handleUserDataUpdate);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('userDataUpdated', handleUserDataUpdate);
    };
  }, []);

  // Refresh user data when component mounts to ensure we have the latest data
  useEffect(() => {
    if (isAuthenticated()) {
      refreshUserData();
      fetchUserBusinesses();
    }
  }, [fetchUserBusinesses]); // Remove isAuthenticated from dependencies since it's a function


  // Get user's language preference
  const getUserLanguage = () => {
    if (user?.language) return user.language;
    return currentLanguage || 'en';
  };

  const userLanguage = getUserLanguage();


  // Function to handle language change
  const handleLanguageChange = async (newLanguage) => {
    try {
      // Update language context
      changeLanguage(newLanguage);
      
      // Update user's language preference in database if user is logged in
      if (user?.id) {
        const response = await fetch('/api/users/update-language', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ language: newLanguage })
        });
        
        if (response.ok) {
          console.log('Language updated successfully in database');
        } else {
          console.log('Failed to update language in database, but context updated');
        }
      }
    } catch (error) {
      console.error('Error updating language:', error);
    }
  };

  // Monitor scroll position in business modal
  useEffect(() => {
    if (!showBusinessOptionsModal) return;

    console.log('🚀 Business options modal opened - setting up scroll monitoring');
    console.log('🚀 Modal config - isAddingSecondBusiness:', userHasBusinessType);
    console.log('🚀 Modal config - onAddSecondBusiness:', !!chatActions.handleAddSecondBusiness);
    console.log('🚀 Modal state - showBusinessOptionsModal:', showBusinessOptionsModal);

    const modalContent = document.querySelector('.business-modal-content');
    if (!modalContent) return;

    const handleModalScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = modalContent;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
      // This will be handled by the business logic hook
    };

    // Check initial scroll position
    handleModalScroll();

    modalContent.addEventListener('scroll', handleModalScroll);
    return () => modalContent.removeEventListener('scroll', handleModalScroll);
  }, [showBusinessOptionsModal, userHasBusinessType, chatActions.handleAddSecondBusiness]);

  // Monitor scroll position in quick options modal


  // Update quick options every time user sends a message or starts a new chat
  useEffect(() => {
    if (messages.length > 0 || businessType) {
      // This will be handled by the business logic hook
    }
  }, [messages.length, businessType, isAdmin]);



  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: clear localStorage and redirect
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
  };


  // Handle deleting a single message
  const handleDeleteMessage = (messageId) => {
    console.log('Deleting message:', messageId);
    // Remove the message from the local state
    setMessages(prev => prev.filter(msg => msg.id !== messageId));
  };

  // Handle voice state changes from ChatInput
  const handleVoiceStateChange = useCallback((newVoiceState) => {
    setVoiceState(newVoiceState);
  }, []);

  // Handle TTS state changes from AIMessageBubble
  const handleTTSStateChange = useCallback((newTtsState) => {
    setTtsState(newTtsState);
  }, []);

  // Handle input mode changes from ChatInput
  const handleInputModeChange = useCallback((newInputMode) => {
    setInputMode(newInputMode);
  }, []);

  // Handle marking messages as read when switching to voice mode
  const handleMarkMessagesAsRead = useCallback(() => {
    console.log('TTS Debug: Marking all existing messages as read');
    // This will be handled by individual AIMessageBubble components
  }, []);

  // Auto-restart function after TTS ends
  const handleAutoRestart = useCallback(() => {
    console.log('TTS Debug: Auto-restarting voice mode after TTS');
    
    // Check if Voice Auto mode was active before TTS
    const wasAutoRecording = voiceState.isAutoRecording;
    
    // Reset all voice-related states to initial state
    setVoiceState({
      isRecording: false,
      isAutoRecording: false,
      hasStartedManually: false,
      isAutoDisabled: false
    });
    
    // Reset TTS state
    setTtsState({
      isReading: false,
      isPaused: false,
      isLoading: false
    });
    
    // DON'T reset inputMode here - let it stay as user intended
    // inputMode should remain as 'keyboard' if user typed with keyboard
    
    // Auto-click microphone ONLY if Voice Auto mode was active AND user is in voice mode
    if (wasAutoRecording && inputMode === 'voice') {
      console.log('TTS Debug: Voice Auto mode was active and user is in voice mode, auto-clicking microphone');
      setTimeout(() => {
        const micButton = document.querySelector('.voice-btn');
        if (micButton && !micButton.disabled) {
          console.log('TTS Debug: Auto-clicking microphone button for new conversation');
          micButton.click();
        } else {
          console.log('TTS Debug: Microphone button not found or disabled');
        }
      }, 500); // 0.5 second delay to ensure states are reset
    } else if (wasAutoRecording && inputMode === 'keyboard') {
      console.log('TTS Debug: Voice Auto mode was active but user switched to keyboard mode, not auto-clicking microphone');
    } else {
      console.log('TTS Debug: Voice Auto mode was not active, waiting for user to speak');
    }
  }, [voiceState.isAutoRecording, inputMode]);

  return (
    <div className="full-chat-container" style={{ background: theme.primaryBg }}>
      {/* Header */}
      <ChatHeader 
        user={user} 
        onLogout={handleLogout}
        theme={theme}
        currentThemeName={currentThemeName}
        userLanguage={userLanguage}
        onLanguageClick={handleLanguageChange}
        onNewChat={handleNewChat}
        onResetWelcome={resetWelcomePopup}
        isAdmin={isAdmin}
        onStartProductTour={handleStartProductTour}
        onHistoryClick={() => setShowChatHistory(true)}
        onMobileMenuChange={setMobileMenuOpen}
      />

      {/* Welcome Popup */}
      <WelcomePopup
        showWelcomePopup={showWelcomePopup}
        handleCloseWelcomePopup={handleCloseWelcomePopup}
        handlePopupBackdropClick={handlePopupBackdropClick}
        handleStartProductTour={handleStartProductTour}
        theme={theme}
        currentThemeName={currentThemeName}
      />

      {/* Fixed Sidebar Menu - Right Side (Desktop Only) */}
      <div className="desktop-sidebar">
        <SidebarMenu
          theme={theme}
          currentThemeName={currentThemeName}
          isAdmin={isAdmin()}
          isToggling={isToggling}
          toggleTheme={toggleTheme}
          handleLogout={handleLogout}
        />
      </div>


      {/* Main Chat Area */}
      <div className="chat-main chat-area-limited" style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: 'calc(100vh - 80px)', 
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div ref={chatScrollContainerRef} className="chat-main-scroll">
        {/* New Chat Button and Quick Options Row - When conversation started */}
        {(messages.length > 0 || businessType) && (
          <div className="conversation-started-row" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            padding: '16px 32px 0 32px',
            paddingBottom: '12px'
          }}>
            {/* Quick Options */}
            <div className="mobile-quick-options" style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '6px',
              justifyContent: 'center',
              width: '100%'
            }}>
              <QuickOptions 
                changingOptions={changingOptions}
                onQuickOption={handleQuickOptionWithScroll}
                onViewAll={() => setShowQuickOptionsPopup(true)}
                isConversationStarted={true}
              />
            </div>
            
            {/* Right Side Buttons - New Chat and Add Business (Desktop Only) */}
            <div className="desktop-button-group mobile-button-group" style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              alignItems: 'flex-end',
              marginTop: '-40px'
            }}>

              {/* New Chat Button */}
              <button
                onClick={handleNewChat}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '16px',
                  padding: '12px 16px',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.4)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(34, 197, 94, 0.3)';
                }}
              >
                <Plus size={14} />
{t('fullchat.newChat')}
              </button>

              {/* Business Type Buttons */}
              <BusinessTypeButtons
                loadingBusinesses={loadingBusinesses}
                userBusinesses={userBusinesses}
                user={user}
                businessType={businessType}
                setBusinessType={setBusinessType}
                setMessages={setMessages}
                handleCloseBusinessOptionsModal={handleCloseBusinessOptionsModal}
                handleRemoveSecondBusiness={handleRemoveSecondBusinessWithScroll}
                handleAddPrimaryBusiness={handleAddPrimaryBusinessWithScroll}
                handleRemovePrimaryBusiness={handleRemovePrimaryBusinessWithScroll}
                theme={theme}
                setShowBusinessOptionsModal={setShowBusinessOptionsModal}
                setShowSecondBusinessModal={setShowSecondBusinessModal}
                setShowPrimaryBusinessModal={setShowPrimaryBusinessModal}
                refreshUserData={refreshUserFromBackend}
                scrollToBottom={scrollToBottom}
              />
            </div>
          </div>
        )}

        {/* Business Type Buttons - Always show for authenticated users without primary business */}
        {isAuthenticated() && !user?.business_type && messages.length === 0 && !businessType && (
          <div className="mobile-business-buttons" style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            padding: '16px 32px 0 32px',
            paddingBottom: '12px'
          }}>
            <BusinessTypeButtons
              loadingBusinesses={loadingBusinesses}
              userBusinesses={userBusinesses}
              user={user}
              businessType={businessType}
              setBusinessType={setBusinessType}
              setMessages={setMessages}
              handleCloseBusinessOptionsModal={handleCloseBusinessOptionsModal}
              handleRemoveSecondBusiness={handleRemoveSecondBusinessWithScroll}
              handleAddPrimaryBusiness={handleAddPrimaryBusinessWithScroll}
              handleRemovePrimaryBusiness={handleRemovePrimaryBusinessWithScroll}
              theme={theme}
              setShowBusinessOptionsModal={setShowBusinessOptionsModal}
              setShowSecondBusinessModal={setShowSecondBusinessModal}
              setShowPrimaryBusinessModal={setShowPrimaryBusinessModal}
              refreshUserData={refreshUserFromBackend}
              scrollToBottom={scrollToBottom}
            />
          </div>
        )}

        {/* Quick Options - When conversation hasn't started */}
        {!businessType && messages.length === 0 && (
          <>
            {/* Welcome Message Spacer */}
            <div style={{
              height: '20px',
              width: '100%'
            }} />
            
            {/* Quick Options Row */}
            <div className="quick-options-row mobile-quick-options" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '24px',
              margin: '20px 0',
              position: 'relative',
              flexDirection: window.innerWidth <= 768 ? 'row' : 'row',
              flexWrap: window.innerWidth <= 768 ? 'wrap' : 'nowrap'
            }}>
              <QuickOptions 
                changingOptions={changingOptions}
                onQuickOption={handleQuickOptionWithScroll}
                onViewAll={() => setShowQuickOptionsPopup(true)}
                isConversationStarted={false}
              />
            </div>


          </>
        )}
        
        {/* Messages */}
        <ChatMessages 
          messages={messages}
          isLoading={isLoading}
          businessType={businessType}
          messagesEndRef={messagesEndRef}
          chatMessagesRef={chatMessagesRef}
          onDeleteMessage={handleDeleteMessage}
          currentConversationId={currentConversationId}
          voiceState={voiceState}
          onTTSStateChange={handleTTSStateChange}
          onAutoRestart={handleAutoRestart}
          inputMode={inputMode}
          onInputModeChange={handleInputModeChange}
          onVoiceStateChange={handleVoiceStateChange}
          onMarkMessagesAsRead={handleMarkMessagesAsRead}
        />
        </div>

        {/* Scroll to Bottom Button */}
        {showScrollButton && (
          <button
            onClick={scrollToBottom}
            className="scroll-to-bottom-btn"
            style={{
              background: theme.primaryButton,
              color: 'white',
              boxShadow: `0 4px 16px ${theme.primaryShadow}`
            }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = `0 6px 20px ${theme.primaryShadow}`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = `0 4px 16px ${theme.primaryShadow}`;
            }}
          >
            <ChevronDown size={20} />
          </button>
        )}
















              

              
              
              
              
              
              



        {/* Input Area */}
        <ChatInput 
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          isLoading={isLoading}
          onSendMessage={handleSendMessageWithScroll}
          enableAutoRecording={true}
          onVoiceStateChange={handleVoiceStateChange}
          ttsState={ttsState}
          onInputModeChange={handleInputModeChange}
          onMarkMessagesAsRead={handleMarkMessagesAsRead}
        />
      </div>

      {/* Footer */}
      <ChatFooter onLanguageClick={() => setShowLanguageSelector(true)} />



      {/* Business Options Modal */}
      <BusinessOptionsModal 
        showBusinessOptionsModal={showBusinessOptionsModal}
        setShowBusinessOptionsModal={setShowBusinessOptionsModal}
        onBusinessTypeSelect={handleBusinessTypeSelectWithScroll}
        isAtBottom={isAtBottom}
        scrollBusinessModal={scrollBusinessModal}
        theme={theme}
      />

      {/* Second Business Selection Modal */}
      <SecondBusinessSelectionModal
        showModal={showSecondBusinessModal}
        setShowModal={setShowSecondBusinessModal}
        onBusinessSelect={handleAddSecondBusinessWithScroll}
        theme={theme}
        userPrimaryBusiness={user?.business_type}
      />

      {/* Primary Business Selection Modal */}
      <PrimaryBusinessSelectionModal
        showModal={showPrimaryBusinessModal}
        setShowModal={setShowPrimaryBusinessModal}
        onBusinessSelect={handleAddPrimaryBusinessWithScroll}
        theme={theme}
      />

      {/* Quick Options Popup */}
      <QuickOptionsPopup
        showQuickOptionsPopup={showQuickOptionsPopup}
        setShowQuickOptionsPopup={setShowQuickOptionsPopup}
        onQuickOptionSelect={handleQuickOptionSelect}
      />


      {/* Product Tour Modal */}
      <ProductTour
        isOpen={showProductTour}
        onClose={handleCompleteProductTour}
        onComplete={handleCompleteProductTour}
      />

      {/* Language Selector Modal */}
      <LanguageSelector
        isOpen={showLanguageSelector}
        onClose={() => setShowLanguageSelector(false)}
        onLanguageClick={handleLanguageChange}
      />

      {/* All Chats Modal */}
      <AllChatsModal
        isOpen={showChatHistory}
        onClose={() => setShowChatHistory(false)}
      />


      {/* Floating Help Button — sadece desktop; mobilde header'da ? var */}
      {!isNarrowViewport && (
      <button
        onClick={handleStartProductTour}
        className="floating-help-button"
        style={{
          position: 'fixed',
          bottom: '32px',
          right: '32px',
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #3b82f6 0%, #22c55e 100%)',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          display: mobileMenuOpen ? 'none' : 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '24px',
          boxShadow: `0 8px 32px ${theme.primaryShadow}`,
          transition: 'all 0.3s ease',
          zIndex: 1000,
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = 'scale(1.1) translateY(-4px)';
          e.currentTarget.style.boxShadow = `0 12px 40px ${theme.primaryShadow}`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = 'scale(1) translateY(0)';
          e.currentTarget.style.boxShadow = `0 8px 32px ${theme.primaryShadow}`;
        }}
        title={t('fullchat.startProductTour')}
      >
        ?
      </button>
      )}



    </div>
  );
}

export default FullChatPage;